class ActiveCouponBxgy {
  constructor() {
    this.syncing = false;
    this.skipNextProductFormSync = false;
    this.rules = this.getRules();

    if (!this.rules.length) return;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:updated', this.handleCartUpdate);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }
  }

  getRules() {
    const rules = [];
    const seen = new Set();

    document.querySelectorAll('[data-active-coupon-bxgy-rules]').forEach((script) => {
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(parsed)) return;

        parsed.forEach((rule) => {
          const normalized = this.normalizeRule(rule);
          if (!normalized) return;

          const key = `${normalized.id}:${normalized.freeVariantId}`;
          if (seen.has(key)) return;

          seen.add(key);
          rules.push(normalized);
        });
      } catch (error) {
        console.warn('ActiveCouponBxgy: invalid rule payload', error);
      }
    });

    return rules;
  }

  normalizeRule(rule) {
    const freeVariantId = this.normalizeId(rule?.freeVariantId);
    const buyProductIds = Array.isArray(rule?.buyProductIds)
      ? rule.buyProductIds.map((id) => this.normalizeId(id)).filter(Boolean)
      : [];

    if (!freeVariantId || !buyProductIds.length) return null;

    return {
      id: String(rule.id || freeVariantId),
      title: String(rule.title || 'Free gift'),
      buyProductIds,
      buyQuantity: Math.max(Number.parseInt(rule.buyQuantity, 10) || 1, 1),
      freeVariantId,
      freeQuantity: Math.max(Number.parseInt(rule.freeQuantity, 10) || 1, 1),
    };
  }

  normalizeId(id) {
    if (id === null || typeof id === 'undefined') return '';
    return String(id).split('/').pop();
  }

  handleCartUpdate(event) {
    const source = this.getEventSource(event);

    if (this.skipNextProductFormSync && source === 'product-form-component') {
      this.skipNextProductFormSync = false;
      return;
    }

    if (source !== 'product-form-component') return;

    this.sync();
  }

  getEventSource(event) {
    return event?.source || event?.detail?.source || event?.detail?.data?.source || '';
  }

  getCartAddPayload({ formData, productId, sections } = {}) {
    if (!(formData instanceof FormData)) return null;

    const mainItem = this.getMainCartItem(formData);
    const normalizedProductId = this.normalizeId(productId);
    const giftItems = this.getGiftItemsForProduct(normalizedProductId, mainItem.quantity);

    if (!mainItem.id || !normalizedProductId || !giftItems.length) {
      this.skipNextProductFormSync = false;
      return null;
    }

    this.skipNextProductFormSync = true;

    return {
      items: [mainItem, ...giftItems],
      ...(sections?.length ? { sections } : {}),
      sections_url: window.location.pathname,
    };
  }

  getMainCartItem(formData) {
    const item = {
      id: this.normalizeId(formData.get('id')),
      quantity: Math.max(Number.parseInt(formData.get('quantity'), 10) || 1, 1),
    };
    const properties = {};
    const sellingPlan = formData.get('selling_plan');

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)]$/);
      if (!match || value === null || value === '') return;
      properties[match[1]] = value;
    });

    if (Object.keys(properties).length) item.properties = properties;
    if (sellingPlan) item.selling_plan = sellingPlan;

    return item;
  }

  getGiftItemsForProduct(productId, addedQuantity = 1) {
    if (!productId) return [];

    return this.rules
      .filter((rule) => rule.buyProductIds.includes(productId))
      .filter((rule) => addedQuantity >= rule.buyQuantity)
      .map((rule) => ({
        id: rule.freeVariantId,
        quantity: rule.freeQuantity,
        properties: this.getGiftProperties(rule),
      }));
  }

  async sync() {
    if (this.syncing || !this.rules.length) return;

    this.syncing = true;

    try {
      const cart = await this.getCart();
      const missingGifts = this.rules
        .filter((rule) => this.qualifiesForRule(cart, rule))
        .filter((rule) => !this.hasGiftLine(cart, rule))
        .map((rule) => ({
          id: rule.freeVariantId,
          quantity: rule.freeQuantity,
          properties: this.getGiftProperties(rule),
        }));

      if (!missingGifts.length) return;

      const response = await this.addGifts(missingGifts);
      this.updateCartUi(response);
    } catch (error) {
      console.warn('ActiveCouponBxgy: unable to auto-add free gift', error);
    } finally {
      this.syncing = false;
    }
  }

  async getCart() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  qualifiesForRule(cart, rule) {
    const qualifyingQuantity = (cart.items || []).reduce((total, item) => {
      const productId = this.normalizeId(item.product_id);
      if (!rule.buyProductIds.includes(productId)) return total;

      return total + (Number.parseInt(item.quantity, 10) || 0);
    }, 0);

    return qualifyingQuantity >= rule.buyQuantity;
  }

  hasGiftLine(cart, rule) {
    return (cart.items || []).some((item) => {
      const variantId = this.normalizeId(item.variant_id);
      const properties = item.properties || {};

      return variantId === rule.freeVariantId && properties._bxgy_free_gift_rule === rule.id;
    });
  }

  getGiftProperties(rule) {
    return {
      _bxgy_free_gift: 'true',
      _bxgy_free_gift_rule: rule.id,
      _bxgy_free_gift_title: rule.title,
    };
  }

  getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }

    const sections = new Set();
    document.querySelectorAll('cart-items-component, cart-drawer-items').forEach((component) => {
      if (component.dataset.sectionId) sections.add(component.dataset.sectionId);
    });
    sections.add('cart-icon-bubble');

    return Array.from(sections);
  }

  async addGifts(items) {
    const body = {
      items,
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    };

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('json')
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      ...config,
      body: JSON.stringify(body),
    });

    const json = await response.json();
    if (!response.ok || json.status) {
      throw new Error(json.description || json.message || 'Unable to add free gift');
    }

    return json;
  }

  updateCartUi(response) {
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerIsOpen = cartDrawer?.classList.contains('open') || cartDrawer?.classList.contains('active');

    if (drawerIsOpen && cartDrawer && typeof cartDrawer.renderContents === 'function' && response.sections) {
      cartDrawer.renderContents(response);
    } else {
      this.updateStaticSections(response.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'active-coupon-bxgy',
        cartData: response,
      });
    }
  }

  updateStaticSections(sections) {
    if (!sections) return;

    Object.entries(sections).forEach(([id, html]) => {
      if (id === 'cart-drawer') return;

      const target = document.getElementById(id);
      if (target) {
        target.innerHTML = html;
      }
    });
  }
}

if (!window.activeCouponBxgy) {
  window.activeCouponBxgy = new ActiveCouponBxgy();
}
